import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  formatPartnerPlanAmount,
  getPartnerSubscriptionPlanPresentation,
} from '../../../utils/partnerSubscriptionPlans';
import { usePortalTokens } from '../../../styles/usePortalTokens';
import { getPortalCardSx } from '../../../styles/portalSurfaces';
import { getPartnerIconBadgeSx } from '../partnerDashboardTheme';

const PartnerPlanCard = ({
  plan,
  isCurrent = false,
  isBusy = false,
  expiresAtLabel = null,
  onSelect,
}) => {
  const portal = usePortalTokens();
  const presentation = getPartnerSubscriptionPlanPresentation();
  const currency = plan.currency || 'AED';
  const periodLabel = plan.billingPeriodMonths === 1 ? 'mo' : `${plan.billingPeriodMonths || 1} mo`;
  const resolvedExpiresAtLabel = expiresAtLabel || plan.expiresAtLabel || null;

  return (
    <Box
      sx={{
        ...getPortalCardSx(portal, {
          borderRadius: '16px',
          maxWidth: 560,
          width: '100%',
          mx: 'auto',
          borderColor: isCurrent ? portal.accentMuted : portal.border,
        }),
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        p: { xs: 2.25, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          <Box sx={getPartnerIconBadgeSx(portal)}>
            <BusinessOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                lineHeight: 1.3,
                color: 'text.primary',
                mb: 0.35,
                wordBreak: 'break-word',
              }}
            >
              {plan.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                lineHeight: 1.45,
                color: 'text.secondary',
              }}
            >
              {presentation.description}
            </Typography>
          </Box>
        </Box>

        {isCurrent ? (
          <Chip
            size="small"
            label="Active"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              height: 26,
              bgcolor: portal.accentSoft,
              color: portal.accent,
              border: `1px solid ${portal.accentMuted}`,
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 0.75,
          mb: resolvedExpiresAtLabel ? 1.75 : 2.25,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '2rem', sm: '2.25rem' },
            lineHeight: 1,
            color: 'text.primary',
            letterSpacing: '-0.02em',
          }}
        >
          {formatPartnerPlanAmount(plan.amount)}
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.9375rem',
            color: 'text.secondary',
          }}
        >
          {currency}/{periodLabel}
        </Typography>
      </Box>

      {resolvedExpiresAtLabel ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 1.25,
            mb: 2.25,
            borderRadius: '12px',
            bgcolor: portal.accentSoft,
            border: `1px solid ${portal.accentMuted}`,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              bgcolor: portal.surface,
              color: portal.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              border: `1px solid ${portal.border}`,
            }}
          >
            <EventAvailableOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: portal.accent,
                lineHeight: 1.2,
                mb: 0.25,
              }}
            >
              Access expires
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.9375rem',
                lineHeight: 1.25,
                color: 'text.primary',
              }}
            >
              {resolvedExpiresAtLabel}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.15,
          mb: 2.75,
          flex: 1,
          pt: 0.25,
        }}
      >
        {presentation.features.map((feature) => (
          <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 18, color: portal.accent, mt: '1px', flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.45 }}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>

      {isCurrent ? (
        <Button
          fullWidth
          disabled
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '10px',
            py: 1.15,
            borderColor: portal.inputBorder,
            color: 'text.secondary',
          }}
        >
          Current plan
        </Button>
      ) : (
        <LoadingButton
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          loading={isBusy}
          onClick={() => onSelect?.(plan)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '10px',
            py: 1.15,
          }}
        >
          Pay portal fee
        </LoadingButton>
      )}
    </Box>
  );
};

export default PartnerPlanCard;
